import { useState, useEffect } from "react";
import {
    Button,
    Grid,
    TextField,
    Stack
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { styled } from '@mui/system';
import { PhoneNumberInput } from "../components/PhoneNumberInput";

const SearchButton = styled(Button)({
    border: '1px solid',
    borderRadius: 0,
    '&:hover': {
        backgroundColor: '#D6D9DD',
        color: '#000000',
        borderColor: '#ADADAD'
    }
});

const DEFAULT_SEARCH_CRITERIA = {
    customerId: '',
    lastName: '',
    businessName: '',
    phoneNumber: '',
    storeId: '',
    vin: '',
    fleetUnitId: '',
    startDate: null,
    firstName: '',
    licensePlate: '',
    documentNumber: '',
    productId: '',
    productSearch: '',
    endDate: null
}
const dateInputs = {
    productId: 'productId',
    productSearch: 'productSearch'
}

export default function Search(props) {
    const { getData, clearData } = props;

    const [searchCriteria, setSearchCriteria] = useState(DEFAULT_SEARCH_CRITERIA);
    const [datesRequired, setDatesRequired] = useState(false);
    const [lastNameRequired, setLastNameRequired] = useState(false);
    const [searchDisabled, setSearchDisabled] = useState(true);
    const [storeIdRequired, setStoreIdRequired] = useState(false);
    const [singleDateRequired, setSingleDateRequired] = useState(false);

    const handleChange = (id, value) => {
        setSearchCriteria((prevState) => {
            return {
                ...prevState,
                [id]: value
            }
        })
    }

    const clearSearch = () => {
        setSearchCriteria(DEFAULT_SEARCH_CRITERIA);
        clearData();
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            if (!searchDisabled) {
                getData(searchCriteria);
            }
        }
    }

    const validDates = () => {
        const dateValid = (date) => !isNaN(date);

        const dates = [
            searchCriteria.startDate,
            searchCriteria.endDate
        ];

        for (const date of dates) {
            if (date && !dateValid(date)) {
                return false;
            }
        }

        return true;
    }

    const searchCriteriaEmpty = () => {
        const emptySearchCriteriaValues = ['', null];        
        const nonEmptySearchCriteria = Object.values(searchCriteria).filter((value) => !emptySearchCriteriaValues.includes(value));
        return nonEmptySearchCriteria.length === 0;
    }

    useEffect(() => {
        setDatesRequired(false);
        setLastNameRequired(false);
        setStoreIdRequired(false);
        setSingleDateRequired(false);
      
        for (const [key, value] of Object.entries(searchCriteria)) {
          // do not consider empty values
          if (!value) {
            continue;
          }
      
          if (key === dateInputs.productId || key === dateInputs.productSearch) {
            // check startDate and endDate
            setDatesRequired(true);
            if (searchCriteria.startDate && searchCriteria.endDate) {
              setDatesRequired(false);
            }
          }
      
          if (key === 'firstName') {
            // check last name
            setLastNameRequired(true);
            if (searchCriteria.lastName) {
              setLastNameRequired(false);
            }
      
            // check storeId + at least one date
            if (searchCriteria.storeId && (searchCriteria.startDate || searchCriteria.endDate)) {
              setStoreIdRequired(false);
              setSingleDateRequired(false);
              setLastNameRequired(false);
            } else {
              // if no last name, require both storeId and at least one date
              if (!searchCriteria.lastName) {
                if (!searchCriteria.storeId) {
                  setStoreIdRequired(true);
                }
                if (!searchCriteria.startDate && !searchCriteria.endDate) {
                  setSingleDateRequired(true);
                }
              }
            }
          }
        }
      }, [searchCriteria]);
      

    useEffect(() => {
        if (searchCriteriaEmpty()) {
            setSearchDisabled(true);
            return;
        }
        if (!validDates()) {
            setSearchDisabled(true);
            return;
        }
        if (datesRequired) {
            setSearchDisabled(true);
            return;
        }
        if (lastNameRequired) {
            setSearchDisabled(true);
            return;
        }
        if (storeIdRequired) {
            setStoreIdRequired(true);
            return;
        }
        if (singleDateRequired) {
            setSingleDateRequired(true);
            return;
        }
        setSearchDisabled(false);
    }, [searchCriteria, datesRequired, lastNameRequired, storeIdRequired, singleDateRequired])

    const buildInputs = () => {
        const inputs = [
            { label: 'Last Name', type: 'text', id: 'lastName' },
            { label: 'First Name', type: 'text', id: 'firstName' },
            { label: 'Business Name', type: 'text', id: 'businessName' },
            { label: 'Phone Number', type: 'phoneNumber', id: 'phoneNumber' },
            { label: 'License Plate', type: 'text', id: 'licensePlate' },
            { label: 'VIN', type: 'text', id: 'vin' },
            { label: 'Fleet Unit Id', type: 'text', id: 'fleetUnitId' },
            { label: 'Document Number', type: 'text', id: 'documentNumber' },
            { label: 'Store Number', type: 'text', id: 'storeId' },
            { label: 'Product Code', type: 'text', id: 'productId' },
            { label: 'Product Description', type: 'text', id: 'productSearch' },
            { label: 'Start Date', type: 'date', id: 'startDate' },
            { label: 'End Date', type: 'date', id: 'endDate' },
            { label: 'Customer Id', type: 'text', id: 'customerId' }
        ];

        return inputs.map((parameters) => {
            const { label, type, id } = parameters;
            let input;
            if (type === 'text') {
                if (id === 'fleetUnitId') {
                    input = <TextField
                                value={searchCriteria[id]}
                                id={id}
                                fullWidth
                                label={label}
                                onKeyDown={handleKeyDown}
                                onChange={(e) => handleChange(id, e.target.value)}
                                disabled={searchCriteriaEmpty()}
                                inputProps={{
                                    style: {
                                    textTransform: "uppercase"
                                    }
                                }}    
                            />;
                } else if (id === 'lastName') {
                    const backgroundColor = lastNameRequired ? 'yellow' : null;
                    input = <TextField
                                value={searchCriteria[id]}
                                id={id}
                                sx={{ width: '100%', backgroundColor }}
                                label={label}
                                onKeyDown={handleKeyDown}
                                onChange={(e) => handleChange(id, e.target.value)}
                                inputProps={{
                                    style: {
                                    textTransform: "uppercase"
                                    }
                                }}    
                            />;
                } else if (id === 'storeId') {
                    const backgroundColor = storeIdRequired ? 'yellow' : null;
                    input = <TextField
                                value={searchCriteria[id]}
                                id={id}
                                sx={{ width: '100%', backgroundColor }}
                                label={label}
                                onKeyDown={handleKeyDown}
                                onChange={(e) => handleChange(id, e.target.value)}
                                inputProps={{
                                    style: {
                                    textTransform: "uppercase"
                                    }
                                }}    
                            />;
                } else {
                    input = <TextField
                                value={searchCriteria[id]}
                                id={id}
                                fullWidth
                                label={label}
                                onKeyDown={handleKeyDown}
                                onChange={(e) => handleChange(id, e.target.value)}
                                inputProps={{
                                    style: {
                                    textTransform: "uppercase"
                                    }
                                }}    
                            />;
                }
                
            } else if (type === 'date') {
                const backgroundColor = datesRequired || singleDateRequired ? 'yellow' : null;
                input = <DatePicker
                            value={searchCriteria[id]}
                            id={id}
                            label={label}
                            sx={{ width: '100%', backgroundColor }}
                            slotProps={{
                                actionBar: { actions: ['clear'] }
                              }}
                            onChange={(date) => handleChange(id, date)}
                        />;
                            
            } else if (type === 'phoneNumber') {
                input = <PhoneNumberInput
                            value={searchCriteria[id]}
                            id={id}
                            label={label}
                            onKeyDown={handleKeyDown}
                            handleChange={handleChange}
                        />;
            }
            return (
                <Grid key={id} item xs={1} >
                    {input}
                </Grid>
            )
        })
    }
    
    return (
        <>
            <Grid
                container
                columns={7}
                spacing={1}
                alignItems="center"
                sx={{ marginTop: 1 }}
            >
                {buildInputs()}
            
            </Grid>
            <Stack
                direction="row"
                marginTop={2}
                spacing={2}
                sx={{
                    justifyContent: "flex-end",
                    alignItems: "center"
                }}
            >
                <SearchButton
                    disabled={searchCriteriaEmpty()}
                    variant='contained'
                    sx={{
                        color: '#000000',
                        backgroundColor: '#F2F3F4',
                        borderColor: '#CCCCCC',
                        '&.Mui-disabled': {
                            backgroundColor: '#F2F3F4',
                            color: '#FFFFFF',
                            fontWeight: 600,
                        }
                    }}
                    onClick={clearSearch}
                >
                    CLEAR
                </SearchButton>
                <SearchButton
                    disabled={searchDisabled}
                    sx={{
                        marginLeft: '5px',
                        borderColor: '#1A2226',
                        '&.Mui-disabled': {
                            backgroundColor: '#849096',
                            color: '#FFFFFF',
                            borderColor: '#6A6F72'
                        }
                    }}
                    variant='contained'
                    onClick={() => getData(searchCriteria)}
                >
                    SEARCH
                </SearchButton>
            </Stack>
        </>
    )
}